import React, { useEffect, useMemo, useRef, useState } from 'react';
import './whatsapp-clone.css';
// Same badge the portal's profile preview uses for Official Business Accounts.
import verifiedBadge from '../../../../images/whatsapp-verify-batch.png';
// Reuses the WhatsApp mark the portal already ships rather than adding another copy.
import whatsappIcon from '../../../../images/whatsapp-circle.png';

const API_BASE = `${process.env.REACT_APP_API_ENDPOINT}/v1`;
// const API_BASE = `https://wabafbm-test1.its.com.pk/v1`;


// A widget session token is valid 12h server-side; past that the persisted token is
// discarded and the key (still in the URL) is re-exchanged for a fresh one.
const SESSION_TTL_MS = 12 * 60 * 60 * 1000;

const HISTORY_PAGE_SIZE = 50;

const BUSINESS_CATEGORIES = {
	ALCOHOL: 'Alcoholic Beverages',
	APPAREL: 'Clothing and Apparel',
	AUTO: 'Automotive',
	BEAUTY: 'Beauty, Spa and Salon',
	EDU: 'Education',
	ENTERTAIN: 'Entertainment',
	EVENT_PLAN: 'Event Planning and Service',
	FINANCE: 'Finance and Banking',
	GOVT: 'Public Service',
	GROCERY: 'Food and Grocery',
	HEALTH: 'Medical and Health',
	HOTEL: 'Hotel and Lodging',
	NONPROFIT: 'Non-profit',
	ONLINE_GAMBLING: 'Online Gambling & Gaming',
	OTC_DRUGS: 'Over-the-Counter Drugs',
	PHYSICAL_GAMBLING: 'Non-Online Gambling & Gaming',
	PROF_SERVICES: 'Professional Services',
	RESTAURANT: 'Restaurant',
	RETAIL: 'Shopping and Retail',
	TRAVEL: 'Travel and Transportation',
	OTHER: 'Other',
};

function businessCategoryLabel(vertical) {
	return BUSINESS_CATEGORIES[vertical] || 'Other';
}

const DEFAULT_CONTACT = {
	name: 'Business',
	online: true,
	avatar: 'B',
	logo: null,
	verified: false,
};

// Country codes offered by the number form. Pakistan leads and is the default — the
// printed QR codes are a domestic campaign — with the rest of the common destinations
// after it for anyone roaming.
const DEFAULT_DIAL_CODE = '92';

const DIAL_CODES = [
	{ code: '92', label: 'Pakistan', flag: '🇵🇰' },
	{ code: '971', label: 'United Arab Emirates', flag: '🇦🇪' },
	{ code: '966', label: 'Saudi Arabia', flag: '🇸🇦' },
	{ code: '968', label: 'Oman', flag: '🇴🇲' },
	{ code: '974', label: 'Qatar', flag: '🇶🇦' },
	{ code: '973', label: 'Bahrain', flag: '🇧🇭' },
	{ code: '965', label: 'Kuwait', flag: '🇰🇼' },
	{ code: '44', label: 'United Kingdom', flag: '🇬🇧' },
	{ code: '1', label: 'United States / Canada', flag: '🇺🇸' },
	{ code: '91', label: 'India', flag: '🇮🇳' },
	{ code: '880', label: 'Bangladesh', flag: '🇧🇩' },
	{ code: '90', label: 'Turkey', flag: '🇹🇷' },
	{ code: '60', label: 'Malaysia', flag: '🇲🇾' },
	{ code: '61', label: 'Australia', flag: '🇦🇺' },
	{ code: '49', label: 'Germany', flag: '🇩🇪' },
	{ code: '33', label: 'France', flag: '🇫🇷' },
	{ code: '39', label: 'Italy', flag: '🇮🇹' },
	{ code: '34', label: 'Spain', flag: '🇪🇸' },
	{ code: '86', label: 'China', flag: '🇨🇳' },
	{ code: '27', label: 'South Africa', flag: '🇿🇦' },
];

// The backend keys a customer on the digits it is given, so 03001234567 and
// 923001234567 would resolve to two different customers — the wrong one showing an
// empty conversation and leaving the agent a duplicate contact. Everything is folded
// to full international digits here: a trunk 0 (or a 00 international prefix) is
// dropped, and the dial code is only prepended when it isn't already there, so a
// pasted +92300… and a typed 0300… land on the same customer.
function normalizePhone(dialCode, raw) {
	let digits = String(raw || '').replace(/[^0-9]/g, '');
	if (!digits) return '';

	const cc = String(dialCode || DEFAULT_DIAL_CODE).replace(/[^0-9]/g, '');

	if (digits.startsWith('00')) digits = digits.slice(2);
	else if (digits.startsWith('0')) digits = digits.slice(1);

	if (cc && digits.startsWith(cc)) return digits;

	return `${cc}${digits}`;
}

function formatTime(dt) {
	if (!dt) return '';
	const d = new Date(dt);
	const hours = d.getHours() % 12 || 12;
	const minutes = String(d.getMinutes()).padStart(2, '0');
	const suffix = d.getHours() >= 12 ? 'PM' : 'AM';
	return `${hours}:${minutes} ${suffix}`;
}

function formatDateLabel(dt) {
	if (!dt) return '';
	const d = new Date(dt);
	const today = new Date();
	const isSameDay = (a, b) => a.toDateString() === b.toDateString();
	if (isSameDay(d, today)) return 'TODAY';
	const yesterday = new Date(today);
	yesterday.setDate(today.getDate() - 1);
	if (isSameDay(d, yesterday)) return 'YESTERDAY';
	return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase();
}

// A history row (WebMessagesCrudService.chat()) exposes `attachments` as an array of
// {attribute_name, attribute_value}; a live socket push exposes a single `attachment_url`
// field instead — this normalizes either shape into the {name, preview, isImage, isVideo,
// isAudio} shape the message bubble renderer expects.
function extractAttachment(m) {
	if (Array.isArray(m.attachments) && m.attachments.length) {
		const get = name => m.attachments.find(a => a.attribute_name === name)?.attribute_value;
		const url = get('url');
		if (!url) return null;
		const type = get('type') || m.message_type;
		return {
			name: get('filename') || 'attachment',
			size: 0,
			type: get('mime_type') || type,
			preview: url,
			isImage: type === 'image',
			isVideo: type === 'video',
			isAudio: type === 'audio',
		};
	}

	if (m.attachment_url) {
		const type = m.message_type;
		return {
			name: m.attachment_url.split('/').pop().split('?')[0],
			size: 0,
			type,
			preview: m.attachment_url,
			isImage: type === 'image',
			isVideo: type === 'video',
			isAudio: type === 'audio',
		};
	}

	return null;
}

// This is a customer-facing page reached from a link or a printed QR, so the tab has
// to read as WhatsApp — not as the agent portal it happens to be routed inside, whose
// index.html titles every tab "Olen Global" and flies the ITS favicon.
const PAGE_TITLE = 'WhatsApp';

// Collapses every chat notification into a single OS-level entry instead of stacking
// one per message.
const NOTIFICATION_TAG = 'waba-webchat-message';

// The same tone the portal plays for agents - it already ships in /public.
const NOTIFICATION_SOUND_URL = '/assets/sounds/chat-notification.mp3';

// A chatbot flow often pushes several messages back to back; one tone per burst.
const NOTIFICATION_SOUND_MIN_GAP_MS = 1500;

// What a notification says for a message with no text of its own.
function notificationBody(message) {
	if (message.message_body) return message.message_body;

	switch (message.message_type) {
		case 'image': return '📷 Photo';
		case 'video': return '🎥 Video';
		case 'audio': return '🎤 Voice message';
		case 'document': return '📄 Document';
		case 'location': return '📍 Location';
		default: return 'New message';
	}
}

let localIdCounter = 0;

// Normalizes a history row or a live socket push into the single shape the thread
// keeps in state. Live pushes may omit direction (they are always business -> customer),
// so a fallback direction covers them.
function normalizeMessage(m, fallbackDirection) {
	localIdCounter += 1;
	return {
		id: m.id || m.message_id || `m-${Date.now()}-${localIdCounter}`,
		message_id: m.message_id,
		direction: m.direction || fallbackDirection || 'outbound',
		dt: m.dt || new Date().toISOString(),
		message_body: m.message_body || '',
		message_type: m.message_type,
		status: m.status,
		attachments: m.attachments,
		attachment_url: m.attachment_url,
		location: m.location,
		interactive: m.interactive,
	};
}

// direction: 'inbound' = customer -> business (the customer's own message, renders right/"out");
// direction: 'outbound' = business -> customer (agent/bot reply, renders left/"in").
function buildThread(messages) {
	const sorted = [...(messages || [])].sort((a, b) => new Date(a.dt) - new Date(b.dt));
	const items = [];
	let lastDateLabel = null;

	sorted.forEach(m => {
		const dateLabel = formatDateLabel(m.dt);
		if (dateLabel !== lastDateLabel) {
			items.push({ type: 'date', label: dateLabel });
			lastDateLabel = dateLabel;
		}

		items.push({
			id: m.id,
			messageId: m.message_id,
			type: 'message',
			from: m.direction === 'inbound' ? 'out' : 'in',
			text: m.message_body || '',
			time: formatTime(m.dt),
			tick: m.direction === 'inbound' ? (m.status === 'read' ? 'read' : 'delivered') : undefined,
			attachment: extractAttachment(m),
			location: m.message_type === 'location' ? m.location : null,
			interactive: m.message_type === 'interactive' ? m.interactive : null,
		});
	});

	return items;
}

// Flattens an interactive (chatbot) message's button/list options into {id, title} pairs.
function interactiveOptions(interactive) {
	if (!interactive) return [];
	if (interactive.type === 'button') {
		const buttons = interactive.action?.buttons || [];
		return buttons.map(b => ({ id: b.reply?.id || b.id, title: b.reply?.title || b.title }));
	}
	if (interactive.type === 'list') {
		const rows = [];
		(interactive.action?.sections || []).forEach(section => {
			(section.rows || []).forEach(row => rows.push({ id: row.id, title: row.title }));
		});
		return rows;
	}
	return [];
}

const EMOJIS = [
	'😀','😃','😄','😁','😆','😅','😂','🤣',
	'😊','😇','🙂','🙃','😉','😌','😍','🥰',
	'😘','😗','😙','😚','😋','😛','😝','😜',
	'🤪','🤨','🧐','🤓','😎','🥳','😏','😒',
	'😞','😔','😟','😕','🙁','☹️','😣','😖',
	'😫','😩','🥺','😢','😭','😤','😠','😡',
	'🤬','🤯','😳','🥵','🥶','😱','😨','😰',
	'😥','😓','🤗','🤔','🫡','🤭','🤫','🤥',
	'😶','😐','😑','😬','🙄','😯','😦','😧',
	'😮','😲','🥱','😴','🤤','😪','😵','🤐',
	'🥴','🤢','🤮','🤧','😷','❤️','🧡','💛',
	'💚','💙','💜','🖤','🤍','🤎','💯','🔥',
	'👍','👎','👏','🙏','🎉','✨','💔','💖',
	'💪','👋','🤝','👌','✌️','☀️','⭐','🎯',
];

function formatBytes(bytes) {
	if (!bytes) return '0 KB';

	if (bytes < 1024 * 1024) {
		return `${Math.max(1, Math.round(bytes / 1024))} KB`;
	}

	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* =========================
   ICONS
========================= */

function BackIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M19 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H19v-2z" />
		</svg>
	);
}

function BellIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M12 22a2.2 2.2 0 0 0 2.19-2.2H9.81A2.2 2.2 0 0 0 12 22zm6.7-5.3v-4.9a6.7 6.7 0 0 0-5.2-6.53v-.72a1.5 1.5 0 0 0-3 0v.72A6.7 6.7 0 0 0 5.3 11.8v4.9L3.6 18.4v.6h16.8v-.6z" />
		</svg>
	);
}

function MoreIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<circle cx="12" cy="5" r="1.7" />
			<circle cx="12" cy="12" r="1.7" />
			<circle cx="12" cy="19" r="1.7" />
		</svg>
	);
}

function SmileIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<circle cx="12" cy="12" r="9" />
			<circle cx="8.7" cy="10" r="1" className="fill" />
			<circle cx="15.3" cy="10" r="1" className="fill" />
			<path d="M7.5 14.2c1.1 1.5 2.6 2.3 4.5 2.3s3.4-.8 4.5-2.3" />
		</svg>
	);
}

function PaperclipIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 11-2.83-2.83l8.49-8.48" />
		</svg>
	);
}

function CameraIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M9 4l1.2-2h3.6L15 4h3a3 3 0 013 3v10a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h3zm3 4.2A4.8 4.8 0 1016.8 13 4.8 4.8 0 0012 8.2z" />
		</svg>
	);
}

function DocumentIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M6 2h8l5 5v15H6a2 2 0 01-2-2V4a2 2 0 012-2zm7 1.5V8h4.5L13 3.5zM8 12h8v1.5H8V12zm0 3h8v1.5H8V15zm0 3h5v1.5H8V18z" />
		</svg>
	);
}

function ImageIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm0 2v12h16V6H4zm3 2.5A1.5 1.5 0 117 11.5a1.5 1.5 0 010-3zM5 18l4-4 3 3 2-2 5 3H5z" />
		</svg>
	);
}

function StickerIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M20 4H8a4 4 0 00-4 4v12h12a8 8 0 008-8V4h-4zm-4 14v-4h4a6 6 0 01-4 4z" />
		</svg>
	);
}

function MicIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M12 15a3 3 0 003-3V6a3 3 0 00-6 0v6a3 3 0 003 3z" />
			<path d="M5 11a7 7 0 0014 0M12 18v3M8 21h8" />
		</svg>
	);
}

function SendIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
		</svg>
	);
}

function CloseIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M18.3 5.71L12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.3 19.71 2.89 18.3 9.17 12 2.89 5.71 4.3 4.29 10.59 10.6l6.3-6.31z" />
		</svg>
	);
}

function DownloadIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M11 3h2v10l3.5-3.5 1.4 1.4L12 16.8l-5.9-5.9 1.4-1.4L11 13V3zM4 19h16v2H4v-2z" />
		</svg>
	);
}

function CheckIcon({ read = false }) {
	return (
		<svg
			className={read ? 'wac-check wac-check-read' : 'wac-check'}
			viewBox="0 0 18 12"
			aria-hidden="true"
		>
			<path d="M1 6l3.5 3.5L11 2" />
			<path d="M7 6l3.5 3.5L17 2" />
		</svg>
	);
}

function PlayIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M8 5v14l11-7z" />
		</svg>
	);
}

function PauseIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M6 5h4v14H6zm8 0h4v14h-4z" />
		</svg>
	);
}

/* =========================
   AUDIO MESSAGE (voice note player)
========================= */

function formatAudioDuration(seconds) {
	if (!isFinite(seconds) || seconds <= 0) return '0:00';
	const m = Math.floor(seconds / 60);
	const s = Math.floor(seconds % 60).toString().padStart(2, '0');
	return `${m}:${s}`;
}

function AudioMessage({ src }) {
	const audioRef = useRef(null);
	const [playing, setPlaying] = useState(false);
	const [duration, setDuration] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return undefined;

		const onTime = () => setCurrentTime(audio.currentTime);
		const onLoaded = () => setDuration(audio.duration || 0);
		const onEnd = () => {
			setPlaying(false);
			setCurrentTime(0);
		};

		audio.addEventListener('timeupdate', onTime);
		audio.addEventListener('loadedmetadata', onLoaded);
		audio.addEventListener('ended', onEnd);

		return () => {
			audio.removeEventListener('timeupdate', onTime);
			audio.removeEventListener('loadedmetadata', onLoaded);
			audio.removeEventListener('ended', onEnd);
		};
	}, []);

	function toggle(event) {
		event.stopPropagation();
		const audio = audioRef.current;
		if (!audio) return;

		if (playing) {
			audio.pause();
			setPlaying(false);
		} else {
			audio.play();
			setPlaying(true);
		}
	}

	function seek(event) {
		event.stopPropagation();
		const audio = audioRef.current;
		if (!audio || !duration) return;

		const bar = event.currentTarget;
		const rect = bar.getBoundingClientRect();
		const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
		audio.currentTime = ratio * duration;
		setCurrentTime(audio.currentTime);
	}

	const progress = duration ? (currentTime / duration) * 100 : 0;

	return (
		<div className="wac-audio-message">
			<audio ref={audioRef} src={src} preload="metadata" />

			<button type="button" className="wac-audio-play" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
				{playing ? <PauseIcon /> : <PlayIcon />}
			</button>

			<div className="wac-audio-waveform" onClick={seek}>
				<div className="wac-audio-progress" style={{ width: `${progress}%` }} />
			</div>

			<span className="wac-audio-time">
				{formatAudioDuration(playing || currentTime ? currentTime : duration)}
			</span>
		</div>
	);
}

/* =========================
   APP
========================= */

export default function WhatsAppCloneApp() {
	const params = new URLSearchParams(window.location.search);
	// The redirect delivers ?client=88&key=aB3xK9... — `clientId` is kept as a fallback
	// so older links that used ?clientId=..&number=.. keep working.
	const linkKey = params.get('key');
	const clientId = params.get('client') || params.get('clientId');
	const urlNumber = params.get('number');
	const urlName = params.get('name') || '';
	// wa.me-style prefilled opening message, and whether it is posted for the customer.
	// `send=0` parks it in the composer instead; absent (or anything else) auto-sends.
	const urlText = params.get('text') || undefined;
	const urlAutoSend = params.get('send') !== '0';

	const [messages, setMessages] = useState([]);
	const [draft, setDraft] = useState('');

	const [emojiOpen, setEmojiOpen] = useState(false);
	const [attachmentOpen, setAttachmentOpen] = useState(false);
	const [headerMenuOpen, setHeaderMenuOpen] = useState(false);

	// Drives the header's bell button: 'default' means the customer has not answered
	// the prompt yet, so there is still something to ask for.
	const [notifPermission, setNotifPermission] = useState(() =>
		('Notification' in window ? Notification.permission : 'unsupported')
	);

	// "Contact info" sheet: the business's WhatsApp profile as Meta holds it (the same
	// fields the portal's Profile page edits). null until loaded / when unavailable.
	const [businessProfile, setBusinessProfile] = useState(null);
	const [businessProfileLoading, setBusinessProfileLoading] = useState(false);
	const [infoOpen, setInfoOpen] = useState(false);

	const [attachment, setAttachment] = useState(null);
	const [recording, setRecording] = useState(false);

	const [messageMenu, setMessageMenu] = useState(null);

	const [contact, setContact] = useState(DEFAULT_CONTACT);
	const [ready, setReady] = useState(false);
	const [showForm, setShowForm] = useState(false);
	const [formPhone, setFormPhone] = useState(urlNumber || '');
	const [formDialCode, setFormDialCode] = useState(DEFAULT_DIAL_CODE);
	const [formName, setFormName] = useState(urlName);
	const [formError, setFormError] = useState(null);
	const [formSubmitting, setFormSubmitting] = useState(false);
	const [loadError, setLoadError] = useState(null);
	const [sending, setSending] = useState(false);

	const [agentTyping, setAgentTyping] = useState(false);
	const [hasOlder, setHasOlder] = useState(false);
	const [loadingOlder, setLoadingOlder] = useState(false);
	const [answeredInteractive, setAnsweredInteractive] = useState({});

	const sessionRef = useRef(null);
	const socketRef = useRef(null);
	const wsUrlRef = useRef(null);
	const historyPageRef = useRef(0);
	const historyTotalPagesRef = useRef(1);
	const keyExchangeRef = useRef(null);
	const typingTimerRef = useRef(null);
	const lastTypingPingRef = useRef(0);
	const mediaRecorderRef = useRef(null);
	const recordedChunksRef = useRef([]);

	const bodyRef = useRef(null);
	const inputRef = useRef(null);

	// Browser-notification state: the one live Notification, whether the permission ask
	// has already been made, and the tab title to restore after a title flash.
	const notificationRef = useRef(null);
	const swRegistrationRef = useRef(null);
	const permissionAskedRef = useRef(false);
	// Seeded with the page's own title, not the portal's, so restoring after an unread
	// title flash puts "WhatsApp" back rather than "Olen Global".
	const originalTitleRef = useRef(PAGE_TITLE);
	const unreadRef = useRef(0);
	const soundRef = useRef(null);
	const soundUnlockedRef = useRef(false);
	const lastSoundAtRef = useRef(0);
	// The socket handler is registered once, so it would otherwise close over the branding
	// and readiness of whichever render connected it.
	const contactRef = useRef(DEFAULT_CONTACT);
	const readyRef = useRef(false);

	const imageInputRef = useRef(null);
	const documentInputRef = useRef(null);
	const mediaInputRef = useRef(null);

	const thread = useMemo(() => buildThread(messages), [messages]);

	const keyStorageName = linkKey
		? `xt_wa_link_key_session_${linkKey}`
		: `xt_wa_link_session_${clientId}`;

	function loadStoredSession() {
		try {
			const stored = JSON.parse(localStorage.getItem(keyStorageName));
			if (stored && stored.sessionToken && Date.now() - stored.savedAt < SESSION_TTL_MS) {
				return stored;
			}
		} catch (e) { /* corrupt/absent — fall through */ }
		return null;
	}

	function persistSession(session) {
		try {
			localStorage.setItem(keyStorageName, JSON.stringify({ ...session, savedAt: Date.now() }));
		} catch (e) { /* localStorage unavailable */ }
	}

	function clearStoredSession() {
		try { localStorage.removeItem(keyStorageName); } catch (e) { /* noop */ }
	}

	// One POST /whatsapp-link/session/from-key returns everything the page needs:
	// sessionToken, visitorId (the customer's WhatsApp number), customerId, name,
	// businessName, avatar, websocketUrl. Deduped so parallel 401 retries share a
	// single exchange.
	function exchangeKey() {
		if (!keyExchangeRef.current) {
			keyExchangeRef.current = (async () => {
				try {
					const res = await fetch(`${API_BASE}/whatsapp-link/session/from-key`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						// ?text= rides the key flow too, so a campaign link can carry a
						// prefilled message without a second code path. autoSendMessage
						// only means anything alongside a message, so it is omitted with none.
						body: JSON.stringify({
							key: linkKey,
							message: urlText,
							autoSendMessage: urlText ? urlAutoSend : undefined,
						}),
					});
					const json = await res.json().catch(() => ({}));
					if (!res.ok) throw json;
					const data = json && json.data !== undefined ? json.data : json;
					if (!data || !data.sessionToken) throw data;

					sessionRef.current = data.sessionToken;
					persistSession(data);
					return data;
				} finally {
					keyExchangeRef.current = null;
				}
			})();
		}
		return keyExchangeRef.current;
	}

	// Talks straight to the API with fetch, not CoreHttpHandler — this page is opened
	// by an anonymous customer (no portal login, no xt-user-token), so it deliberately
	// stays outside the authenticated app's HTTP layer, same as the embeddable widget's
	// loader.js. A 401 (expired 12h token) re-exchanges the key and retries once.
	async function api(path, options = {}, isRetry = false) {
		const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
		if (sessionRef.current) headers['xt-widget-session-token'] = sessionRef.current;

		const res = await fetch(`${API_BASE}${path}`, {
			method: options.method || 'GET',
			headers,
			body: options.body ? JSON.stringify(options.body) : undefined,
		});

		const json = await res.json().catch(() => ({}));
		if (!res.ok) {
			// This API renames Boom's `statusCode` to `code` (ErrorDecorator), so the
			// body is read that way; the HTTP status is still the primary signal.
			const status = res.status || (json && json.code);

			if (status === 401 && !isRetry) {
				if (linkKey) {
					// Returning visitor whose 12h token lapsed: re-exchange and retry the
					// failed call so they never see the interruption.
					await exchangeKey();
					return api(path, options, true);
				}
				// Keyless QR entry with nothing to re-exchange — the only case where the
				// visitor has to do something, and all it costs is their number again.
				handleSessionLost();
			}

			throw json;
		}
		return json && json.data !== undefined ? json.data : json;
	}

	// Token gone and unrecoverable: drop the stale session and fall back to the number
	// form (or a terminal error if the URL can't even identify the business).
	function handleSessionLost() {
		sessionRef.current = null;
		clearStoredSession();
		setReady(false);
		if (clientId) setShowForm(true);
		else setLoadError('This chat session has expired — please open the link again.');
	}

	// Rewrites the address bar to the resumable form of this link. Dropping ?text= and
	// ?send= is deliberate: the session call runs on every load, so leaving them in the
	// URL would re-post the opening message on a plain refresh.
	function rewriteUrlWithKey(key) {
		// Nothing to normalize on a plain ?client= link that produced no key and carried
		// no prefilled text.
		if (!key && !urlText) return;

		try {
			const next = new URLSearchParams();
			if (clientId) next.set('client', String(clientId));
			if (key) next.set('key', key);
			// text/send are dropped even when no key came back, so a backend that can't
			// mint one still can't leave a refresh re-posting the opening message.
			const query = next.toString();
			window.history.replaceState(
				null,
				'',
				query ? `${window.location.pathname}?${query}` : window.location.pathname
			);
		} catch (e) { /* history unavailable — the chat still works */ }
	}

	// `prefillMessage` non-null means the text was NOT posted — either send=0 asked for
	// it to sit in the composer, or the auto-send failed server-side. Both want the same
	// thing, which is why this never inspects `messageSent`. When it IS null the text is
	// already in the history response, so appending it here would render it twice.
	function applyPrefill(session) {
		if (!session || !session.prefillMessage) return;

		setDraft(session.prefillMessage);
		focusComposer();
	}

	// The composer only mounts once the form is swapped out, so this retries across a
	// few frames rather than assuming React has already re-rendered.
	function focusComposer(attempt = 0) {
		const input = inputRef.current;
		if (!input) {
			if (attempt < 5) requestAnimationFrame(() => focusComposer(attempt + 1));
			return;
		}

		input.focus();
		try {
			input.setSelectionRange(input.value.length, input.value.length);
		} catch (e) { /* selection unsupported on this input type */ }
	}

	function applyBranding(source) {
		const name = source.businessName || 'Business';
		setContact({
			name,
			online: true,
			avatar: name.trim().charAt(0).toUpperCase() || 'B',
			logo: source.avatar || null,
		});
	}

	// GET /whatsapp-link/business-profile -> { profile } (about, address, description,
	// email, websites, vertical, profile_picture_url). Best-effort: a client that isn't
	// onboarded on Cloud API gets a 400 and the sheet just shows name + avatar.
	async function loadBusinessProfile() {
		setBusinessProfileLoading(true);
		try {
			const res = await api('/whatsapp-link/business-profile');
			const profile = res && res.profile ? res.profile : null;
			setBusinessProfile(profile);
			if (profile) {
				setContact(prev => ({
					...prev,
					// Config branding wins; the profile only fills gaps.
					name: prev.name && prev.name !== 'Business' ? prev.name : (profile.businessName || prev.name),
					logo: prev.logo || profile.avatar || profile.profile_picture_url || profile.logo || null,
					// Official Business Account -> verified badge next to the name.
					verified: !!profile.isOfficialBusinessAccount,
				}));
			}
		} catch (e) {
			setBusinessProfile(null);
		} finally {
			setBusinessProfileLoading(false);
		}
	}

	function scrollToBottom() {
		requestAnimationFrame(() => {
			if (bodyRef.current) {
				bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
			}
		});
	}

	// Fire-and-forget: tells the backend the customer has seen the business's messages,
	// which pushes ✓✓ read receipts back to the agent portal.
	function markRead() {
		if (!sessionRef.current) return;
		api('/whatsapp-link/messages/read', { method: 'POST', body: {} }).catch(e => {
			console.error('[whatsapp-link] failed to mark messages read', e);
		});
	}

	// Android Chrome forbids `new Notification()` from a page - it throws an "Illegal
	// constructor" TypeError - so the only way to notify a phone is through a service
	// worker registration. Registering is harmless before permission is granted, and
	// the worker itself intercepts nothing.
	function registerNotificationWorker() {
		if (!('serviceWorker' in navigator)) return;

		navigator.serviceWorker
			.register('/webchat-sw.js', { scope: '/apps/' })
			.then(registration => {
				swRegistrationRef.current = registration;
			})
			.catch(e => {
				console.warn('[whatsapp-link] notification worker did not register', e);
			});
	}

	// Built lazily so a chat that never receives a message never fetches the file.
	function getNotificationSound() {
		if (!soundRef.current) {
			try {
				soundRef.current = new Audio(NOTIFICATION_SOUND_URL);
				soundRef.current.preload = 'auto';
				soundRef.current.volume = 0.6;
			} catch (e) { /* no Audio support - stay silent */ }
		}
		return soundRef.current;
	}

	// Autoplay policy blocks the first programmatic play() until the customer has
	// interacted with the page, so the gesture that raises the permission prompt also
	// primes the tone: play it muted, then rewind. From then on play() is allowed even
	// while the tab sits in the background.
	function unlockNotificationSound() {
		const sound = getNotificationSound();
		if (!sound) return;

		sound.muted = true;
		const settle = () => {
			sound.pause();
			sound.currentTime = 0;
			sound.muted = false;
		};

		try {
			const played = sound.play();
			if (played && typeof played.then === 'function') played.then(settle).catch(settle);
			else settle();
		} catch (e) { settle(); }
	}

	// Both the permission prompt and audio playback need a user gesture, so the first
	// click/keypress on the chat primes them together. Safe to call repeatedly.
	function primeNotifications(force) {
		askNotificationPermission(force);
		if (soundUnlockedRef.current) return;
		soundUnlockedRef.current = true;
		unlockNotificationSound();
	}

	function playNotificationSound() {
		const sound = getNotificationSound();
		if (!sound) return;

		const now = Date.now();
		if (now - lastSoundAtRef.current < NOTIFICATION_SOUND_MIN_GAP_MS) return;
		lastSoundAtRef.current = now;

		try {
			sound.currentTime = 0;
			const played = sound.play();
			// Rejects when the customer has not interacted with the page yet - nothing to do.
			if (played && typeof played.catch === 'function') played.catch(() => {});
		} catch (e) { /* nothing to do */ }
	}

	// The customer counts as "on the chat" only when the tab is visible *and* the thread
	// is actually rendered - the phone form doesn't count.
	function isForeground() {
		return document.visibilityState === 'visible' && readyRef.current;
	}

	// Notification.requestPermission() has to run inside a user gesture (Safari refuses
	// otherwise), so this is called from the customer's first click/keypress on the page.
	// The passive path asks only once, so a dismissed prompt is never re-raised on every
	// stray click; `force` is the header bell, where asking again is the whole point.
	function askNotificationPermission(force) {
		if (!('Notification' in window)) return;
		if (permissionAskedRef.current && !force) return;
		permissionAskedRef.current = true;

		if (Notification.permission !== 'default') {
			setNotifPermission(Notification.permission);
			return;
		}

		try {
			const result = Notification.requestPermission();
			if (result && typeof result.then === 'function') {
				result.then(setNotifPermission).catch(() => {});
			} else {
				// Older Safari answers through a callback and returns undefined.
				setNotifPermission(Notification.permission);
			}
		} catch (e) { /* unsupported - the title flash covers it */ }
	}

	// Returns false when nothing could be shown (unsupported, denied, or a browser that
	// only allows notifications through a service worker), which is the caller's cue to
	// flash the tab title instead.
	function showMessageNotification(message) {
		if (!('Notification' in window) || Notification.permission !== 'granted') return false;

		// Only one notification stays live; `tag` collapses them in the OS tray as well.
		if (notificationRef.current) {
			try { notificationRef.current.close(); } catch (e) { /* already gone */ }
			notificationRef.current = null;
		}

		const branding = contactRef.current || DEFAULT_CONTACT;
		const title = branding.name || 'Business';
		const options = {
			body: notificationBody(message),
			// `avatar` is a single initial when there is no logo - not something to render.
			icon: branding.logo || undefined,
			tag: NOTIFICATION_TAG,
			data: { url: `${window.location.pathname}${window.location.search}` },
		};

		// The worker first: on Android it is the only path that works, and where both
		// work it behaves the same. Its notificationclick handler focuses the chat, so
		// no onclick is wired here.
		const registration = swRegistrationRef.current;
		if (registration && registration.showNotification) {
			registration.showNotification(title, options).catch(() => flashTitle());
			return true;
		}

		try {
			const notification = new Notification(title, options);

			notification.onclick = () => {
				window.focus();
				clearMessageNotification();
				markRead();
			};

			notificationRef.current = notification;
			return true;
		} catch (e) {
			// Android Chrome throws here and the worker never registered - fall back to
			// the tab title.
			return false;
		}
	}

	// No-permission fallback: put the unread count where the customer sees it on the tab strip.
	function flashTitle() {
		unreadRef.current += 1;
		document.title = `🔴 (${unreadRef.current}) New message - ${originalTitleRef.current}`;
	}

	function clearMessageNotification() {
		if (notificationRef.current) {
			try { notificationRef.current.close(); } catch (e) { /* already gone */ }
			notificationRef.current = null;
		}

		// A worker-raised notification is not owned by this page, so it has to be looked
		// up by tag before it can be dismissed.
		const registration = swRegistrationRef.current;
		if (registration && registration.getNotifications) {
			registration
				.getNotifications({ tag: NOTIFICATION_TAG })
				.then(list => list.forEach(item => item.close()))
				.catch(() => {});
		}
		if (unreadRef.current) {
			unreadRef.current = 0;
			document.title = originalTitleRef.current;
		}
	}

	// Throttled so fast typing doesn't spam the backend — one ping per ~2.5s keeps the
	// "typing…" indicator live on the agent's side.
	function pingTyping() {
		if (!sessionRef.current) return;
		const now = Date.now();
		if (now - lastTypingPingRef.current < 2500) return;
		lastTypingPingRef.current = now;
		api('/whatsapp-link/typing', { method: 'POST', body: {} }).catch(e => {
			console.error('[whatsapp-link] failed to send typing ping', e);
		});
	}

	// No explicit "stopped typing" event from the backend — the header just falls back
	// from "typing…" to "online" ~4s after the last typing push.
	function showAgentTyping() {
		setAgentTyping(true);
		clearTimeout(typingTimerRef.current);
		typingTimerRef.current = setTimeout(() => setAgentTyping(false), 4000);
	}

	async function fetchHistoryPage(page) {
		return api('/whatsapp-link/messages/history', {
			method: 'POST',
			body: { page, limit: HISTORY_PAGE_SIZE },
		});
	}

	// Page 0 is the newest slice sorted newest-first, so it's reversed into chronological
	// order before rendering; totalPages tells us whether a "load earlier" button is needed.
	async function loadInitialHistory() {
		const res = await fetchHistoryPage(0);
		const rows = [...(res.messages || [])].reverse().map(m => normalizeMessage(m));
		historyPageRef.current = 0;
		historyTotalPagesRef.current = res.totalPages || 1;
		setMessages(rows);
		setHasOlder((res.totalPages || 1) > 1);
		scrollToBottom();
	}

	async function loadOlderHistory() {
		if (loadingOlder || !hasOlder) return;
		setLoadingOlder(true);

		const nextPage = historyPageRef.current + 1;
		const el = bodyRef.current;
		const prevScrollHeight = el ? el.scrollHeight : 0;
		const prevScrollTop = el ? el.scrollTop : 0;

		try {
			const res = await fetchHistoryPage(nextPage);
			const rows = [...(res.messages || [])].reverse().map(m => normalizeMessage(m));
			historyPageRef.current = nextPage;
			historyTotalPagesRef.current = res.totalPages || historyTotalPagesRef.current;
			setMessages(items => [...rows, ...items]);
			setHasOlder(nextPage + 1 < historyTotalPagesRef.current);

			// Keep the viewport anchored on the message that was at the top before the
			// older batch was prepended, so the view doesn't jump.
			requestAnimationFrame(() => {
				if (el) el.scrollTop = prevScrollTop + (el.scrollHeight - prevScrollHeight);
			});
		} catch (e) {
			console.error('[whatsapp-link] failed to load older messages', e);
		} finally {
			setLoadingOlder(false);
		}
	}

	function openSocket(websocketUrl) {
		if (socketRef.current || !window.io) return;

		const socket = window.io(websocketUrl, {
			// Scope must match the JWT's own scope ('waba-webchat-session', minted by
			// /whatsapp-link/session/*) — the socket server hard-rejects the handshake
			// on a mismatch (the embeddable widget's 'widget-session' is a different token type).
			query: { token: sessionRef.current, scope: 'waba-webchat-session' },
			// Polling does its handshake over several separate HTTP requests, which the
			// Swarm routing mesh can scatter across replicas without sticky sessions
			// (breaks with "Session ID unknown"). A websocket-only connection is a single
			// upgrade request that stays pinned to whichever replica accepted it.
			transports: ['websocket'],
		});

		socket.on('newWebVisitorMessage', payload => {
			if (payload && payload.kind === 'status' && payload.status === 'read') {
				const ids = new Set(payload.messageIds || []);
				setMessages(items =>
					items.map(m => (ids.has(m.message_id) ? { ...m, status: 'read' } : m))
				);
				return;
			}

			if (payload && payload.kind === 'typing') {
				showAgentTyping();
				return;
			}

			// { kind: 'outbound', customerId, message } — a bot/agent message.
			const message = payload && payload.message;
			if (!message) return;

			setAgentTyping(false);
			clearTimeout(typingTimerRef.current);

			setMessages(items => [...items, normalizeMessage(message, 'outbound')]);
			scrollToBottom();

			// A tone on every incoming message, looking at the chat or not - a browser
			// notification's own sound is suppressed on most desktop platforms.
			playNotificationSound();

			// Looking at the chat: business as usual. Away (tab hidden, or on another tab):
			// raise a browser notification, falling back to a tab-title flash when the
			// customer never granted permission.
			if (isForeground()) {
				markRead();
			} else if (!showMessageNotification(message)) {
				flashTitle();
			}
		});

		socketRef.current = socket;
	}

	function connectSocket(websocketUrl) {
		if (!websocketUrl || socketRef.current) return;

		// The socket.io client script only needs to load once per document — a remount
		// (or a second connect) must reuse window.io instead of appending another tag.
		if (window.io) {
			openSocket(websocketUrl);
			return;
		}
		const existing = document.querySelector('script[data-xt-socketio]');
		if (existing) {
			existing.addEventListener('load', () => openSocket(websocketUrl));
			return;
		}

		const script = document.createElement('script');
		script.src = `${websocketUrl}/socket.io/socket.io.js`;
		script.setAttribute('data-xt-socketio', 'true');
		script.onload = () => openSocket(websocketUrl);
		script.onerror = () => {
			console.warn(`[whatsapp-link] could not load socket.io client from ${websocketUrl}`);
		};
		document.head.appendChild(script);
	}

	async function enterChat(websocketUrl) {
		wsUrlRef.current = websocketUrl || wsUrlRef.current;
		await loadInitialHistory();
		setShowForm(false);
		setReady(true);
		loadBusinessProfile();
		connectSocket(wsUrlRef.current);
		markRead();
	}

	// Key flow. The key is re-exchanged on every load rather than trusting a stored
	// token that still looks unexpired — that check is a client-clock comparison and
	// drifts, the key itself never expires, and the exchange is also what carries a
	// link's ?text= through. A stored session is only a fallback for a failed exchange.
	async function initFromKey() {
		let session;
		try {
			session = await exchangeKey();
		} catch (e) {
			session = loadStoredSession();
			if (!session) throw e;
			sessionRef.current = session.sessionToken;
		}

		applyBranding(session);

		try {
			await enterChat(session.websocketUrl);
			rewriteUrlWithKey(linkKey);
			applyPrefill(session);
		} catch (e) {
			// Stored token rejected and re-exchange also failed — drop the stale
			// session so the next attempt starts clean.
			clearStoredSession();
			throw e;
		}
	}

	// Fallback flow (no key, or the exchange failed): branding from config, then a
	// phone-number form that starts the session explicitly.
	async function initFromConfig() {
		const cfg = await api(`/whatsapp-link/config/${clientId}`);
		applyBranding(cfg);
		wsUrlRef.current = cfg.websocketUrl;

		const stored = loadStoredSession();

		// A returning visitor skips the form. The one exception is a link carrying
		// ?text=: that message still has to be delivered, which only session/start
		// does — so their remembered number is replayed through it rather than the
		// text being silently dropped, and they still never see the form.
		if (stored && !urlText) {
			sessionRef.current = stored.sessionToken;
			try {
				await enterChat(stored.websocketUrl || cfg.websocketUrl);
				return;
			} catch (e) {
				sessionRef.current = null;
				clearStoredSession();
			}
		}

		if (stored && urlText && stored.visitorId) {
			try {
				await startPhoneSession(String(stored.visitorId), stored.name || urlName);
				return;
			} catch (e) {
				console.error('[whatsapp-link] failed to resume stored session with prefilled text', e);
				if (e && e.code === 403) throw e;
				sessionRef.current = null;
				clearStoredSession();
			}
		}

		if (urlNumber) {
			try {
				// Legacy ?number= links already carry full international digits — running
				// them through the form's dial code would prepend a second country code.
				await startPhoneSession(String(urlNumber).replace(/[^0-9]/g, ''), urlName);
				return;
			} catch (e) {
				console.error('[whatsapp-link] failed to auto-start session from URL number', e);
				// A disabled client or a blocked visitor cannot be fixed by typing the
				// number again, so it must not fall through to the form.
				if (e && e.code === 403) throw e;
			}
		}

		setShowForm(true);
	}

	async function startPhoneSession(phoneNumber, name) {
		const session = await api('/whatsapp-link/session/start', {
			method: 'POST',
			body: {
				clientId: Number(clientId),
				phoneNumber,
				name: name || undefined,
				message: urlText,
				autoSendMessage: urlText ? urlAutoSend : undefined,
			},
		});
		if (!session || !session.sessionToken) throw session;

		sessionRef.current = session.sessionToken;
		persistSession(session);
		applyBranding(session);

		// The backend mints (or hands back) this visitor's own key, so a keyless QR
		// entry still ends up on a resumable link — coming back skips the number form.
		rewriteUrlWithKey(session.webchatKey);

		await enterChat(session.websocketUrl);
		applyPrefill(session);
	}

	async function submitPhoneForm(event) {
		if (event) event.preventDefault();

		const phone = normalizePhone(formDialCode, formPhone);
		if (!phone) {
			setFormError('Please enter your phone number.');
			return;
		}
		// Shortest plausible international number is 8 digits; catches a half-typed
		// number here rather than boarding a customer that will never match.
		if (phone.length < 8) {
			setFormError('That number looks too short — please check it.');
			return;
		}

		// Still inside the submit gesture, which is what the permission prompt and the
		// audio unlock both need.
		primeNotifications();

		setFormSubmitting(true);
		setFormError(null);
		try {
			await startPhoneSession(phone, formName.trim());
		} catch (e) {
			console.error('[whatsapp-link] failed to start session', e);
			// 403 is terminal — the client is disabled or this visitor is blocked, and
			// resubmitting can only fail identically. It takes over the screen instead
			// of sitting above a live "Start chat" button inviting a retry.
			if (e && e.code === 403) {
				setLoadError(e.message || 'This chat is no longer available.');
				return;
			}
			setFormError((e && e.message) || 'Could not start the chat, please try again shortly.');
		} finally {
			setFormSubmitting(false);
		}
	}

	// Tab identity. Both the title and the favicon are restored on unmount, because the
	// page is still mounted inside the portal SPA — a staff member who opens it and
	// navigates back must not be left with a WhatsApp-branded tab.
	useEffect(() => {
		const previousTitle = document.title;
		document.title = PAGE_TITLE;

		// index.html declares rel="shortcut icon"; the ~= token match catches that as
		// well as a plain rel="icon" if the shell ever changes.
		let iconLink = document.querySelector("link[rel~='icon']");
		const createdIconLink = !iconLink;
		if (!iconLink) {
			iconLink = document.createElement('link');
			iconLink.rel = 'shortcut icon';
			document.head.appendChild(iconLink);
		}

		const previousIcon = iconLink.getAttribute('href');
		iconLink.setAttribute('href', whatsappIcon);

		return () => {
			document.title = previousTitle;
			if (createdIconLink) iconLink.remove();
			else if (previousIcon !== null) iconLink.setAttribute('href', previousIcon);
		};
	}, []);

	useEffect(() => {
		(async () => {
			try {
				if (linkKey) {
					try {
						await initFromKey();
						return;
					} catch (e) {
						console.error('[whatsapp-link] key exchange failed, falling back', e);
						if (!clientId) throw e;
					}
				}

				if (!clientId) {
					setLoadError('This link is missing required information — ask the business for a valid chat link.');
					return;
				}

				await initFromConfig();
			} catch (e) {
				console.error('[whatsapp-link] failed to load chat', e);
				setLoadError((e && e.message) || 'Could not load this chat, please try again shortly.');
			}
		})();

		return () => {
			if (socketRef.current) socketRef.current.disconnect();
			clearTimeout(typingTimerRef.current);
			clearMessageNotification();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Re-send the read receipt whenever the customer comes back to the tab with the
	// chat already open — messages that arrived while they were away get their ✓✓.
	useEffect(() => {
		if (!ready) return undefined;

		const onVisible = () => {
			if (document.visibilityState !== 'visible') return;
			// Back on the chat - retire whatever announced the message while they were away.
			clearMessageNotification();
			markRead();
			// The customer may have flipped the permission in browser settings meanwhile.
			if ('Notification' in window) setNotifPermission(Notification.permission);
		};

		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('focus', onVisible);

		return () => {
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('focus', onVisible);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ready]);

	// The socket handler is registered once, so branding and readiness reach it by ref.
	useEffect(() => {
		contactRef.current = contact;
	}, [contact]);

	useEffect(() => {
		readyRef.current = ready;
	}, [ready]);

	useEffect(() => {
		registerNotificationWorker();
	}, []);

	// The customer's first interaction with the page is the user gesture the permission
	// prompt and the audio unlock both need. Armed on mount rather than on `ready`: the
	// phone form is a perfectly good place to click, and a chat that never finishes
	// loading would otherwise never ask at all.
	useEffect(() => {
		const onGesture = () => primeNotifications();
		document.addEventListener('pointerdown', onGesture);
		document.addEventListener('keydown', onGesture);

		return () => {
			document.removeEventListener('pointerdown', onGesture);
			document.removeEventListener('keydown', onGesture);
		};
	}, []);

	useEffect(() => {
		if (attachment) scrollToBottom();
	}, [attachment]);

	useEffect(() => {
		const close = () => {
			setMessageMenu(null);
			setHeaderMenuOpen(false);
		};

		window.addEventListener('click', close);

		return () => window.removeEventListener('click', close);
	}, []);

	function insertEmoji(emoji) {
		const input = inputRef.current;

		if (!input) {
			setDraft((value) => value + emoji);
			return;
		}

		const start = input.selectionStart ?? draft.length;
		const end = input.selectionEnd ?? draft.length;

		const next =
			draft.substring(0, start) +
			emoji +
			draft.substring(end);

		setDraft(next);

		requestAnimationFrame(() => {
			input.focus();

			const position = start + emoji.length;

			input.setSelectionRange(position, position);
		});
	}

	function chooseFile(file) {
		if (!file) return;

		const isImage = file.type.startsWith('image/');
		const isVideo = file.type.startsWith('video/');

		let preview = null;

		if (isImage || isVideo) {
			preview = URL.createObjectURL(file);
		}

		setAttachment({
			file,
			name: file.name,
			size: file.size,
			type: file.type,
			preview,
			isImage,
			isVideo,
		});

		setAttachmentOpen(false);
		setEmojiOpen(false);

		requestAnimationFrame(() => {
			inputRef.current?.focus();
		});
	}

	function selectImage(event) {
		const file = event.target.files?.[0];

		if (file) chooseFile(file);

		event.target.value = '';
	}

	function selectDocument(event) {
		const file = event.target.files?.[0];

		if (file) chooseFile(file);

		event.target.value = '';
	}

	function removeAttachment() {
		if (attachment?.preview) {
			URL.revokeObjectURL(attachment.preview);
		}

		setAttachment(null);
	}

	async function uploadAttachmentFile(file) {
		const form = new FormData();
		form.append('file', file, file.name || 'attachment');

		const headers = {};
		if (sessionRef.current) headers['xt-widget-session-token'] = sessionRef.current;

		const res = await fetch(`${API_BASE}/whatsapp-link/messages/media/upload`, {
			method: 'POST',
			headers,
			body: form,
		});

		const json = await res.json().catch(() => ({}));
		if (!res.ok) throw json;
		return json && json.data !== undefined ? json.data : json;
	}

	function appendLocalMessage(overrides) {
		localIdCounter += 1;
		const local = {
			id: `local-${Date.now()}-${localIdCounter}`,
			message_id: null,
			direction: 'inbound',
			dt: new Date().toISOString(),
			message_body: '',
			status: 'delivered',
			...overrides,
		};
		setMessages(items => [...items, local]);
		scrollToBottom();
		return local.id;
	}

	// Correlates a just-appended bubble with the server-assigned message id, so a later
	// "read" status push (matched by that same id) can flip this bubble's tick to ✓✓.
	function tagLocalMessage(localId, res) {
		const messageId = res && res.message && res.message.message_id;
		if (!messageId) return;
		setMessages(items => items.map(m => (m.id === localId ? { ...m, message_id: messageId } : m)));
	}

	async function sendMessage() {
		const text = draft.trim();

		if ((!text && !attachment) || !ready || sending) return;

		const pendingAttachment = attachment;
		setSending(true);
		setDraft('');
		setAttachment(null);
		setEmojiOpen(false);
		setAttachmentOpen(false);

		try {
			let attachmentUrl;
			let mimeType;

			if (pendingAttachment) {
				const uploaded = await uploadAttachmentFile(pendingAttachment.file);
				attachmentUrl = uploaded.url;
				mimeType = uploaded.mimeType || pendingAttachment.type;
			}

			const localId = appendLocalMessage({
				message_body: text,
				message_type: pendingAttachment
					? (pendingAttachment.isImage ? 'image' : pendingAttachment.isVideo ? 'video' : 'document')
					: 'text',
				attachment_url: attachmentUrl || undefined,
			});

			const res = await api('/whatsapp-link/messages/send', {
				method: 'POST',
				body: { text: text || undefined, attachmentUrl, mimeType },
			});
			tagLocalMessage(localId, res);
		} catch (e) {
			console.error('[whatsapp-link] failed to send message', e);
		} finally {
			setSending(false);
		}
	}

	// A tapped chatbot button/list row sends the option's id as the reply payload but
	// shows the human-readable title in the customer's own bubble.
	async function sendInteractiveReply(itemId, option) {
		if (!ready || !option.id) return;

		setAnsweredInteractive(prev => ({ ...prev, [itemId]: true }));

		const localId = appendLocalMessage({
			message_body: option.title || option.id,
			message_type: 'text',
		});

		try {
			const res = await api('/whatsapp-link/messages/send', {
				method: 'POST',
				body: { text: option.id },
			});
			tagLocalMessage(localId, res);
		} catch (e) {
			console.error('[whatsapp-link] failed to send interactive reply', e);
		}
	}

	function onInputKeyDown(event) {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			sendMessage();
		}
	}

	function onDraftChange(event) {
		setDraft(event.target.value);
		if (event.target.value.trim()) pingTyping();
	}

	function downloadAttachment(file) {
		if (!file?.preview) return;

		const link = document.createElement('a');

		link.href = file.preview;
		link.download = file.name || 'attachment';

		document.body.appendChild(link);
		link.click();
		link.remove();
	}

	function copyMessage(text) {
		if (navigator.clipboard && text) {
			navigator.clipboard.writeText(text);
		}

		setMessageMenu(null);
	}

	function pickRecorderMimeType() {
		const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
		for (const candidate of candidates) {
			if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(candidate)) {
				return candidate;
			}
		}
		return '';
	}

	async function sendVoiceNote(blob, mimeType) {
		if (!ready || blob.size === 0) return;

		setSending(true);
		try {
			const ext = mimeType.indexOf('mp4') !== -1 ? 'm4a' : 'webm';
			const file = new File([blob], `voice-note.${ext}`, { type: mimeType });
			const uploaded = await uploadAttachmentFile(file);

			const localId = appendLocalMessage({
				message_type: 'audio',
				attachment_url: uploaded.url,
			});

			const res = await api('/whatsapp-link/messages/send', {
				method: 'POST',
				body: { attachmentUrl: uploaded.url, mimeType: uploaded.mimeType || mimeType },
			});
			tagLocalMessage(localId, res);
		} catch (e) {
			console.error('[whatsapp-link] failed to send voice note', e);
		} finally {
			setSending(false);
		}
	}

	async function startRecording() {
		if (!ready || recording) return;

		if (!navigator.mediaDevices || !window.MediaRecorder) {
			alert('Voice notes are not supported in this browser.');
			return;
		}

		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			const mimeType = pickRecorderMimeType();
			const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

			recordedChunksRef.current = [];

			recorder.ondataavailable = (event) => {
				if (event.data && event.data.size > 0) recordedChunksRef.current.push(event.data);
			};

			recorder.onstop = () => {
				const finalMimeType = recorder.mimeType || mimeType || 'audio/webm';
				const blob = new Blob(recordedChunksRef.current, { type: finalMimeType });
				stream.getTracks().forEach((track) => track.stop());
				recordedChunksRef.current = [];
				mediaRecorderRef.current = null;
				setRecording(false);

				if (blob.size > 0) sendVoiceNote(blob, finalMimeType);
			};

			mediaRecorderRef.current = recorder;
			recorder.start();
			setRecording(true);
		} catch (e) {
			console.error('[whatsapp-link] microphone access failed', e);
			alert('Could not access your microphone — please allow microphone access to send a voice note.');
		}
	}

	function stopRecording() {
		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
			mediaRecorderRef.current.stop();
		}
	}

	function toggleRecording(event) {
		event.stopPropagation();
		if (recording) stopRecording();
		else startRecording();
	}

	const canSend =
		Boolean(draft.trim()) || Boolean(attachment);

	if (loadError) {
		return (
			<div className="wac-root" style={{ alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
				<div style={{ padding: 24, textAlign: 'center', color: '#3b4a54' }}>{loadError}</div>
			</div>
		);
	}

	return (
		<div className="wac-root">
			{/* HEADER */}

			<header className="wac-header">
				<div className="wac-header-left">
					<button
						className="wac-header-back"
						type="button"
						aria-label="Back"
					>
						<BackIcon />
					</button>

					<div
						className="wac-avatar"
						style={{ cursor: ready ? 'pointer' : 'default' }}
						onClick={() => ready && setInfoOpen(true)}
					>
						{contact.logo ? <img src={contact.logo} alt={contact.name} onError={() => setContact(prev => ({ ...prev, logo: null }))} /> : contact.avatar}
					</div>

					<div
						className="wac-contact"
						style={{ cursor: ready ? 'pointer' : 'default' }}
						onClick={() => ready && setInfoOpen(true)}
					>
						<div className="wac-contact-name">
							{contact.name}
							{contact.verified && (
								<img src={verifiedBadge} className="wac-verified" alt="Verified business" title="Official Business Account" />
							)}
						</div>

						<div className="wac-contact-status">
							{showForm
								? 'online'
								: !ready
									? 'connecting…'
									: agentTyping
										? 'typing…'
										: contact.online
											? 'online'
											: 'last seen recently'}
						</div>
					</div>
				</div>

				<div className="wac-header-actions">

					{notifPermission === 'default' && (
						<button
							type="button"
							className="wac-header-button"
							aria-label="Turn on notifications"
							title="Turn on notifications"
							onClick={(event) => {
								event.stopPropagation();
								primeNotifications(true);
							}}
						>
							<BellIcon />
						</button>
					)}

					<button
						type="button"
						className="wac-header-button"
						aria-label="More"
						onClick={(event) => {
							event.stopPropagation();
							setHeaderMenuOpen(
								(value) => !value
							);
						}}
					>
						<MoreIcon />
					</button>
				</div>

				{headerMenuOpen && (
					<div
						className="wac-header-menu"
						onClick={(event) =>
							event.stopPropagation()
						}
					>
						<button
							type="button"
							disabled={!ready}
							onClick={() => {
								setHeaderMenuOpen(false);
								setInfoOpen(true);
							}}
						>
							Contact info
						</button>

						<button type="button">
							Close chat
						</button>

					</div>
				)}
			</header>

			{/* BUSINESS INFO SHEET - WhatsApp's "business info" screen, fed by the
			    client's Meta business profile. */}

			{infoOpen && (
				<div className="wac-info" onClick={(event) => event.stopPropagation()}>
					<div className="wac-info-header">
						<button
							className="wac-header-back"
							type="button"
							aria-label="Back"
							onClick={() => setInfoOpen(false)}
						>
							<BackIcon />
						</button>
						<span>Business info</span>
					</div>

					<div className="wac-info-body">
						<div className="wac-info-hero">
							<div className="wac-info-avatar">
								{contact.logo ? <img src={contact.logo} alt={contact.name} onError={() => setContact(prev => ({ ...prev, logo: null }))} /> : contact.avatar}
							</div>
							<div className="wac-info-name">
								{contact.name}
								{contact.verified && (
									<img src={verifiedBadge} className="wac-verified" alt="Verified business" title="Official Business Account" />
								)}
							</div>
							{businessProfile && businessProfile.vertical && businessProfile.vertical !== 'OTHER' && (
								<div className="wac-info-category">
									{businessCategoryLabel(businessProfile.vertical)}
								</div>
							)}
						</div>

						{businessProfile && businessProfile.about && (
							<section className="wac-info-section">
								<div className="wac-info-label">About</div>
								<div className="wac-info-value">{businessProfile.about}</div>
							</section>
						)}

						{businessProfile && businessProfile.description && (
							<section className="wac-info-section">
								<div className="wac-info-label">Description</div>
								<div className="wac-info-value">{businessProfile.description}</div>
							</section>
						)}

						{businessProfile && businessProfile.displayPhoneNumber && (
							<section className="wac-info-section">
								<div className="wac-info-label">Phone</div>
								<div className="wac-info-value">
									<a href={`tel:${businessProfile.displayPhoneNumber.replace(/s+/g, '')}`}>{businessProfile.displayPhoneNumber}</a>
								</div>
							</section>
						)}

						{businessProfile && businessProfile.address && (
							<section className="wac-info-section">
								<div className="wac-info-label">Address</div>
								<div className="wac-info-value">{businessProfile.address}</div>
							</section>
						)}

						{businessProfile && businessProfile.email && (
							<section className="wac-info-section">
								<div className="wac-info-label">Email</div>
								<div className="wac-info-value">
									<a href={`mailto:${businessProfile.email}`}>{businessProfile.email}</a>
								</div>
							</section>
						)}

						{businessProfile && Array.isArray(businessProfile.websites) && businessProfile.websites.length > 0 && (
							<section className="wac-info-section">
								<div className="wac-info-label">Website{businessProfile.websites.length > 1 ? 's' : ''}</div>
								{businessProfile.websites.map((site) => (
									<div className="wac-info-value" key={site}>
										<a href={site} target="_blank" rel="noopener noreferrer">{site}</a>
									</div>
								))}
							</section>
						)}

						{businessProfileLoading && (
							<div className="wac-info-empty">Loading business details…</div>
						)}

						{!businessProfileLoading && !businessProfile && (
							<div className="wac-info-empty">No further business details available.</div>
						)}
					</div>
				</div>
			)}

			{/* PRE-CHAT FORM (fallback flow when there's no key to exchange) */}

			{showForm && (
				<main className="wac-messages">
					<div className="wac-wallpaper" />

					<form className="wac-prechat" onSubmit={submitPhoneForm}>
						{/* Branding from GET /whatsapp-link/config/{clientId} — the visitor
						    scanned a code off a pack or poster, so the business identifies
						    itself before asking for a phone number. */}
						<div className="wac-prechat-brand">
							<div className="wac-prechat-avatar">
								{contact.logo
									? <img src={contact.logo} alt={contact.name} onError={() => setContact(prev => ({ ...prev, logo: null }))} />
									: contact.avatar}
							</div>

							<div className="wac-prechat-title">
								Chat with {contact.name}
							</div>
						</div>

						<div className="wac-prechat-sub">
							Enter your WhatsApp number to start the conversation.
						</div>

						<div className="wac-prechat-phone">
							<select
								value={formDialCode}
								aria-label="Country code"
								onChange={(event) => setFormDialCode(event.target.value)}
							>
								{DIAL_CODES.map((country) => (
									<option key={country.code} value={country.code}>
										{country.flag} +{country.code}
									</option>
								))}
							</select>

							<input
								type="tel"
								inputMode="tel"
								value={formPhone}
								placeholder="300 1234567"
								autoComplete="tel-national"
								onChange={(event) => setFormPhone(event.target.value)}
							/>
						</div>

						<input
							type="text"
							value={formName}
							placeholder="Your name (optional)"
							autoComplete="name"
							onChange={(event) => setFormName(event.target.value)}
						/>

						{formError && (
							<div className="wac-prechat-error">{formError}</div>
						)}

						<button type="submit" disabled={formSubmitting}>
							{formSubmitting ? 'Starting…' : 'Start chat'}
						</button>
					</form>
				</main>
			)}

			{/* MESSAGES */}

			{!showForm && (
				<main
					className="wac-messages"
					ref={bodyRef}
				>
					<div className="wac-wallpaper" />

					{hasOlder && (
						<button
							type="button"
							className="wac-load-more"
							disabled={loadingOlder}
							onClick={loadOlderHistory}
						>
							{loadingOlder ? 'Loading…' : 'Load earlier messages'}
						</button>
					)}

					{thread.map((message) => {
						if (message.type === 'date') {
							return (
								<div
									className="wac-date"
									key={message.label}
								>
									{message.label}
								</div>
							);
						}

						return (
							<div
								className={`wac-message-row ${
									message.from
								}`}
								key={message.id}
							>
								<div
									className={`wac-message ${
										message.from
									}`}
									onContextMenu={(event) => {
										event.preventDefault();
										event.stopPropagation();

										setMessageMenu(
											message.id
										);
									}}
								>
									{message.attachment?.isImage && (
										<div className="wac-media-message">
											<img
												src={
													message
														.attachment
														.preview
												}
												alt={
													message
														.attachment
														.name
												}
												onClick={() =>
													downloadAttachment(
														message.attachment
													)
												}
											/>
										</div>
									)}

									{message.attachment?.isVideo && (
										<div className="wac-video-message">
											<video
												src={
													message
														.attachment
														.preview
												}
												controls
											/>
										</div>
									)}

									{message.attachment?.isAudio && (
										<AudioMessage src={message.attachment.preview} />
									)}

									{message.attachment &&
										!message.attachment
											.isImage &&
										!message.attachment
											.isVideo &&
										!message.attachment
											.isAudio && (
											<div
												className="wac-file-message"
												onClick={() =>
													downloadAttachment(
														message.attachment
													)
												}
											>
												<div className="wac-file-icon">
													<DocumentIcon />
												</div>

												<div className="wac-file-details">
													<div className="wac-file-name">
														{
															message
																.attachment
																.name
														}
													</div>

													<div className="wac-file-size">
														{formatBytes(
															message
																.attachment
																.size
														)}
													</div>
												</div>

												<div className="wac-file-download">
													<DownloadIcon />
												</div>
											</div>
										)}

									{message.location && (
										<a
											className="wac-location-card"
											href={`https://www.google.com/maps?q=${encodeURIComponent(
												`${message.location.latitude},${message.location.longitude}`
											)}`}
											target="_blank"
											rel="noopener noreferrer"
										>
											📍 {message.location.name || 'Location'}
											{message.location.address ? ` — ${message.location.address}` : ''}
										</a>
									)}

									{message.interactive && (
										<>
											{message.interactive.body?.text && (
												<div className="wac-text">
													{message.interactive.body.text}
												</div>
											)}

											{message.interactive.type === 'cta_url' &&
												message.interactive.action?.parameters?.url && (
													<a
														className="wac-location-card"
														href={message.interactive.action.parameters.url}
														target="_blank"
														rel="noopener noreferrer"
													>
														{message.interactive.action.parameters.display_text ||
															message.interactive.action.parameters.url}
													</a>
												)}

											{interactiveOptions(message.interactive).length > 0 && (
												<div className="wac-interactive-options">
													{interactiveOptions(message.interactive).map(option => (
														<button
															type="button"
															key={option.id || option.title}
															disabled={
																message.from !== 'in' ||
																Boolean(answeredInteractive[message.id])
															}
															onClick={() =>
																sendInteractiveReply(message.id, option)
															}
														>
															{option.title || option.id}
														</button>
													))}
												</div>
											)}
										</>
									)}

									{message.text && !message.interactive && (
										<div className="wac-text">
											{message.text}
										</div>
									)}

									<div className="wac-meta">
										<span>
											{message.time}
										</span>

										{message.from ===
											'out' &&
											message.tick && (
												<CheckIcon
													read={
														message.tick ===
														'read'
													}
												/>
											)}
									</div>

									{messageMenu === message.id && (
										<div
											className={`wac-message-menu ${
												message.from ===
												'out'
													? 'right'
													: 'left'
											}`}
											onClick={(event) =>
												event.stopPropagation()
											}
										>
											{message.text && (
												<button
													type="button"
													onClick={() =>
														copyMessage(
															message.text
														)
													}
												>
													Copy
												</button>
											)}
										</div>
									)}
								</div>
							</div>
						);
					})}
				</main>
			)}

			{/* EMOJI PICKER */}

			{emojiOpen && (
				<div className="wac-emoji-picker">
					<div className="wac-emoji-tabs">
						<button className="active">
							🙂
						</button>
					</div>

					<div className="wac-emoji-content">
						<div className="wac-emoji-title">
							Smileys & People
						</div>

						<div className="wac-emoji-grid">
							{EMOJIS.map((emoji, index) => (
								<button
									type="button"
									key={`${emoji}-${index}`}
									onClick={() =>
										insertEmoji(
											emoji
										)
									}
								>
									{emoji}
								</button>
							))}
						</div>
					</div>
				</div>
			)}

			{/* ATTACHMENT MENU */}

			{attachmentOpen && (
				<div className="wac-attachment-menu">
					<button
						type="button"
						onClick={() =>
							imageInputRef.current?.click()
						}
					>
						<span className="wac-attachment-icon purple">
							<ImageIcon />
						</span>

						<span>Photos & videos</span>
					</button>

					<button
						type="button"
						onClick={() =>
							documentInputRef.current?.click()
						}
					>
						<span className="wac-attachment-icon blue">
							<DocumentIcon />
						</span>

						<span>Document</span>
					</button>

					<button
						type="button"
						onClick={() =>
							mediaInputRef.current?.click()
						}
					>
						<span className="wac-attachment-icon red">
							<CameraIcon />
						</span>

						<span>Camera</span>
					</button>

					<button type="button">
						<span className="wac-attachment-icon green">
							<StickerIcon />
						</span>

						<span>Sticker</span>
					</button>
				</div>
			)}

			<input
				ref={imageInputRef}
				className="wac-hidden"
				type="file"
				accept="image/*,video/*"
				onChange={selectImage}
			/>

			<input
				ref={documentInputRef}
				className="wac-hidden"
				type="file"
				onChange={selectDocument}
			/>

			<input
				ref={mediaInputRef}
				className="wac-hidden"
				type="file"
				accept="image/*"
				capture="environment"
				onChange={selectImage}
			/>

			{/* ATTACHMENT PREVIEW */}

			{attachment && (
				<div className="wac-attachment-preview">
					<div className="wac-preview-inner">
						<button
							type="button"
							className="wac-preview-close"
							onClick={removeAttachment}
						>
							<CloseIcon />
						</button>

						{attachment.isImage && (
							<img
								src={attachment.preview}
								alt={attachment.name}
							/>
						)}

						{attachment.isVideo && (
							<video
								src={attachment.preview}
								controls
							/>
						)}

						{!attachment.isImage &&
							!attachment.isVideo && (
								<div className="wac-preview-file">
									<DocumentIcon />
								</div>
							)}

						<div className="wac-preview-info">
							<div className="wac-preview-name">
								{attachment.name}
							</div>

							<div className="wac-preview-size">
								{formatBytes(
									attachment.size
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			{/* COMPOSER */}

			<footer className="wac-composer">
				<div className="wac-composer-field">
					<button
						type="button"
						className="wac-composer-icon"
						aria-label="Emoji"
						onClick={(event) => {
							event.stopPropagation();

							setEmojiOpen(
								(value) => !value
							);

							setAttachmentOpen(false);
						}}
					>
						<SmileIcon />
					</button>

					<textarea
						ref={inputRef}
						value={draft}
						placeholder={ready ? 'Type a message' : 'Connecting…'}
						rows={1}
						disabled={!ready}
						onChange={onDraftChange}
						onKeyDown={onInputKeyDown}
					/>

					<button
						type="button"
						className="wac-composer-icon"
						aria-label="Attach"
						onClick={(event) => {
							event.stopPropagation();

							setAttachmentOpen(
								(value) => !value
							);

							setEmojiOpen(false);
						}}
					>
						<PaperclipIcon />
					</button>
				</div>

				<button
					type="button"
					className={`wac-mic-button ${
						recording ? 'recording' : ''
					}`}
					aria-label={
						canSend ? 'Send' : 'Voice message'
					}
					onClick={
						canSend
							? sendMessage
							: toggleRecording
					}
				>
					{canSend ? (
						<SendIcon />
					) : (
						<MicIcon />
					)}
				</button>
			</footer>
		</div>
	);
}
